import React, {
  useState, useEffect, useMemo, useImperativeHandle, forwardRef,
} from 'react';
import {
  Checkbox, Grid, List, ListItem, ListItemText, Typography,
} from '@mui/material';
import { employeeEntity } from 'shared/entityDetails';
import { AsyncMultipleSelect } from 'shared/components/select/SearchableSelects';
import {
  addEmployeesToGroup,
  deleteUserGroupEmployee,
  getNonGroupEmployees,
  searchUsers,
  updateUserGroupEmployee,
} from 'shared/api/admin';
import { InfoModal } from 'shared/components/abstract/interactables';
import { useAuth, useRequest } from 'shared/hooks';
import { BUTTON_COLORS, VARIANTS as buttonColorEnum } from 'shared/constants';
import StickyHeadTable from 'shared/components/table/StickyHeadTable';
import { Link } from 'react-router-dom';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { UserTableWrapper } from './styled';
import { getUserPath } from '../../shared/routes/admin';

const getEmployeeLink = (employee) => (
  <Link to={getUserPath(employee.id)}>{employee.fullname}</Link>
);

const managerColumnFormatter = (employee, isAdmin, onUpdate) => (
  <Checkbox
    value={employee.id}
    checked={employee.manager}
    disabled={!isAdmin}
    onChange={() => {
      onUpdate(employee.id, {
        ...employee,
        manager: !employee.manager,
      });
    }}
  />
);

const deleteEmployeeAction = (employee, groupName, onDelete) => (
  <InfoModal
    tip="Remove employee from group"
    icon={PersonRemoveIcon}
    className="trash"
    message="Remove employee from group"
    title="Remove Employee"
    actionName="Remove"
    color={BUTTON_COLORS.error}
    description={<p>{`This will remove ${employee.employee.fullname} from "${groupName}". Are you sure?`}</p>}
    actionCallback={() => onDelete(employee.id)}
  />
);

/**
 * @param {Object} props
 * @param {number} props.userGroupId
 * @param {boolean} props.staged
 * @param {string} props.groupName
 * @param {Array<Object>} props.groupEmployees
 * @param {Function} props.onEmployeesToAddChange
 * @param {Object} ref
 */
const UserGroupEmployees = forwardRef(({
  userGroupId = null,
  staged,
  groupName,
  groupEmployees,
  onEmployeesToAddChange,
}, ref) => {
  const {
    user: currentUser,
    isAdmin,
  } = useAuth();

  const {
    request,
  } = useRequest();

  const [employeesToAdd, setEmployeesToAdd] = useState([]);
  const [currentEmployees, setCurrentEmployees] = useState(groupEmployees || []);
  const [updateCount] = React.useState(0);

  useEffect(() => {
    if (userGroupId) {
      setCurrentEmployees(groupEmployees || []);
    } else {
      setCurrentEmployees([]);
    }
  }, [userGroupId, groupEmployees]);

  const updateEmployeesToAdd = (employees) => {
    setEmployeesToAdd(employees);
    if (staged && onEmployeesToAddChange) {
      onEmployeesToAddChange(employees);
    }
  };

  const searchReqauest = useMemo(() => {
    if (!staged && userGroupId) {
      return getNonGroupEmployees;
    }

    return searchUsers;
  }, [staged, userGroupId]);

  const addEmployees = async (groupId) => {
    if (employeesToAdd.length > 0) {
      const employeeIds = employeesToAdd.map((employee) => employee.employeeId);
      const result = await request(addEmployeesToGroup({
        employee_ids: employeeIds,
        user_group_id: groupId,
        created_by_id: currentUser.id,
      }), 'Added employee(s) to user group');
      if (result) {
        setEmployeesToAdd([]);
        setCurrentEmployees(result.user_group_employees || []);
        return result;
      }
      return null;
    }
    return null;
  };

  useImperativeHandle((ref), () => ({
    addEmployees,
  }));

  const updateGroupEmployee = async (groupId, user) => {
    const result = await request(updateUserGroupEmployee(groupId, user), 'Updated employee in user group');
    if (result) {
      const index = currentEmployees.findIndex((e) => e.employee_id === user.employee_id);
      if (index !== -1) {
        currentEmployees[index] = result;
        setCurrentEmployees([...currentEmployees]);
      }
    }
  };

  const deleteGroupEmployee = async (employeeId) => {
    const result = await request(deleteUserGroupEmployee(employeeId), 'Removed employee from user group');
    if (result) {
      const employees = [...currentEmployees];
      const index = employees.findIndex((e) => e.id === employeeId);
      if (index !== -1) {
        employees.splice(index, 1);
      }
      setCurrentEmployees(employees);
    }
  };

  const columns = [
    {
      name: 'Employee Name',
      formatter: (value) => getEmployeeLink(value.employee),
    },
    {
      name: 'Email',
      jsonKey: 'email',
      formatter: (value) => value.employee.email,
    },
    {
      name: 'Manager',
      formatter: (employee) => managerColumnFormatter(employee, isAdmin, updateGroupEmployee),
    },
  ];

  if (isAdmin) {
    columns.push({
      name: 'Remove',
      formatter: (employee) => deleteEmployeeAction(employee, groupName, deleteGroupEmployee),
    });
  }

  const addEmployeeModalContent = () => (
    <>
      This will add:
      <List
        sx={{
          listStyleType: 'disc',
          pl: 4,
        }}
        dense
      >
        {employeesToAdd.map((employee) => (
          <ListItem
            key={employee.employeeId}
            disablePadding
            sx={{ display: 'list-item' }}
          >
            <ListItemText primary={employee.fullname} />
          </ListItem>
        ))}
      </List>
      {`to "${groupName}". Are you sure?`}
    </>
  );

  return (
    <>
      <Grid container size={12} sx={{ justifyContent: 'space-between' }} spacing={1}>
        <Grid size={10}>
          <AsyncMultipleSelect
            searchRequest={searchReqauest}
            getOptionLabel={employeeEntity.getOptionLabel}
            getOptionValue={employeeEntity.getOptionValue}
            onChange={updateEmployeesToAdd}
            label="Add Employees"
            selectedOptions={employeesToAdd}
            routeParam={userGroupId || null}
            updateCount={updateCount}
          />
          {staged && (
            <Typography variant="caption" sx={{ marginLeft: 1.75 }}>
              Employees will be added on group creation
            </Typography>
          )}
        </Grid>
        <Grid sx={{ alignContent: 'center', justifyContent: 'center' }}>
          {(!staged && isAdmin) && (
            <InfoModal
              title="Add Employees"
              actionName="Add"
              actionCallback={() => addEmployees(userGroupId)}
              color={buttonColorEnum.primary}
              disabled={employeesToAdd.length === 0}
              description={addEmployeeModalContent()}
            />
          )}
        </Grid>
      </Grid>
      <UserTableWrapper>
        <StickyHeadTable
          columns={columns}
          noRowsText=""
          rowId="id"
          rows={currentEmployees}
          striped
        />
      </UserTableWrapper>
    </>
  );
});

export default UserGroupEmployees;
